import {getPropertyInstruction} from '../service/PropertyService';
import {workerLabel} from './scheduler/cleanerRules';
import {isLegacyWorker} from '../service/jobScheduling';

export const ASSIGNMENT_MODES = [
    {value: 'FIXED', label: 'Fixed', help: 'Saving assigns all cleaning jobs from tomorrow to this cleaner, including jobs already assigned. New future cleaning jobs also default to this cleaner, even when unavailable. You can change individual assignments in Scheduler.'},
    {value: 'PREFERRED', label: 'Preferred', help: 'This cleaner by default. Auto Assign may replace them only to meet a check-in.'},
];

export const INSTRUCTION_TYPES = [
    {value: 'carpark', label: 'CarPark'},
    {value: 'locker', label: 'Locker'},
    {value: 'other', label: 'Other'},
];

export function cleanerOptions(directory, region, referencedIds) {
    const referenced = new Set(referencedIds.filter(id => id && !isLegacyWorker(id)).map(String));
    return Object.values(directory)
        .filter(entry => !isLegacyWorker(entry.id))
        .filter(entry => referenced.has(String(entry.id)) || (!entry.isDeleted && (!region || entry.region === region)))
        .map(entry => ({id: String(entry.id), label: workerLabel(entry, entry.id) + (entry.region && region && entry.region !== region ? ` (${entry.region})` : '')}))
        .concat([...referenced].filter(id => !directory[id]).map(id => ({id, label: workerLabel(null, id)})));
}

export function isValidDuration(value) {
    return Number.isInteger(value) && value >= 15 && value <= 720 && value % 15 === 0;
}

export function cleanerPayload(rule, skills) {
    const primary = rule.preferred_worker_id && !isLegacyWorker(rule.preferred_worker_id) ? String(rule.preferred_worker_id) : null;
    const backup = rule.backup_worker_id && !isLegacyWorker(rule.backup_worker_id) ? String(rule.backup_worker_id) : null;
    const activeIds = new Set(skills.filter(skill => skill.status === 'ACTIVE').map(skill => String(skill.id)));
    return {
        version: rule.version,
        skill_ids: (rule.skill_ids || []).filter(id => activeIds.has(String(id))).map(Number),
        preferred_worker_id: primary,
        assignment_mode: primary ? (rule.assignment_mode === 'FIXED' ? 'FIXED' : 'PREFERRED') : null,
        backup_worker_id: primary && backup && backup !== primary ? backup : null,
    };
}

export function sectionIsDirty(section) {
    return Boolean(section?.value != null && section?.baseline != null
        && JSON.stringify(section.value) !== JSON.stringify(section.baseline));
}

export async function readPropertyResponse(response, fallback = 'Unable to load property data.') {
    const body = await response.json().catch(() => null);
    if (!response.ok || body == null) {
        const error = new Error(body?.message || body?.detail || body?.error || fallback);
        error.status = response.status;
        error.body = body;
        throw error;
    }
    return body;
}

export async function loadPropertyInstruction(propertyId, type) {
    try {
        const value = await readPropertyResponse(await getPropertyInstruction(propertyId, type), 'Unable to load instructions.');
        return {...value, description: value.description || '', images: value.images || []};
    } catch (error) {
        // All three existing endpoints use this same validation message for an absent instruction.
        // Authorization failures and unavailable endpoints must never look like an empty editable form.
        if (error.status === 400 && error.message === `No car park instruction found for property ID ${propertyId}`) {
            return {description: '', images: []};
        }
        throw error;
    }
}
